import { Suspense } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { BrandLogo } from '../BrandLogo';
import { LoadingState } from '../LoadingState';
import { ThemeToggle } from '../ThemeToggle';

/** What the brand panel says the product does, step by step. */
const STEPS = [
  { icon: 'bi-person-vcard', title: 'Capture the ID', text: 'Front and back of a license, passport or ID card' },
  { icon: 'bi-person-bounding-box', title: 'Selfie and liveness', text: 'A real person, matched to the ID photo' },
  { icon: 'bi-patch-check', title: 'Automatic decision', text: 'Approved or declined, with fraud signals and reasons' },
];

/**
 * The frame of sign-in, password reset, email confirmation and the error pages (since 2026-10-25):
 * a card centred on a soft background, with a brand panel on the left - logo, what InertiaID does,
 * three steps - and the page itself on the right. Below `lg` the panel is dropped, the card fills
 * the screen and the logo sits above the form. The pages keep their
 * own card markup; `.auth-main` in site.css flattens the card so it reads as part of the page.
 */
export function AuthLayout() {
  return (
    <div className="auth-shell">
      <div className="position-absolute top-0 end-0 p-3">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        <aside className="auth-panel d-none d-lg-flex">
          <Link to="/" className="text-decoration-none" aria-label="InertiaID home">
            <BrandLogo size={44} textScale={0.64} />
          </Link>

          <div className="auth-panel-body">
            <h1 className="auth-panel-title">Know who you're dealing with, in seconds.</h1>
            <p className="auth-panel-lead">
              Document checks, liveness and face match, and fraud signals, in one verification API.
            </p>
            <ol className="auth-steps list-unstyled mb-0">
              {STEPS.map((s) => (
                <li key={s.title} className="auth-step">
                  <span className="auth-step-icon">
                    <i className={`bi ${s.icon}`} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="auth-step-title">{s.title}</span>
                    <span className="auth-step-text">{s.text}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <div className="auth-panel-foot">
            <span>&copy; {new Date().getFullYear()} InertiaID</span>
            <span>
              <i className="bi bi-shield-lock me-1" aria-hidden="true" />
              Two-step sign-in
            </span>
          </div>
        </aside>

        <main className="auth-main">
          <div className="auth-form">
            <Link to="/" className="d-lg-none d-inline-block mb-4 text-decoration-none" aria-label="InertiaID home">
              <BrandLogo size={36} />
            </Link>
            <Suspense fallback={<LoadingState />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
